/**
 * Reine Helfer für die Audit-Log-View (ohne DOM, damit testbar). Klassifiziert
 * Actions in Gruppen-Chips, mappt Result-Chips, baut die API-Query und bereitet
 * metadata_json lesbar auf. Redaction passiert serverseitig im Store; hier wird
 * sie nur unverändert durchgereicht (`[redacted]` bleibt sichtbar).
 */

import type { ApiResponse } from "./api";

export type AuditResult = "success" | "failed" | "denied";

/** Eine Audit-Zeile, wie sie die `audit_log`-API liefert (camelCase). */
export interface AuditEntry {
  id: number;
  ts: number;
  actorUserId: number | null;
  actorUsername: string | null;
  actorRole: string | null;
  actorIp: string | null;
  actorUserAgent: string | null;
  serverId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  summary: string | null;
  metadata: unknown;
  result: AuditResult;
  error: string | null;
  createdAt: string;
}

export interface AuditResponse extends ApiResponse {
  entries: AuditEntry[];
  /** Liefert die API IP/User-Agent? Nur mit action.view_sensitive. */
  canViewSensitive?: boolean;
}

/** UI-Filterzustand. `text` ist clientseitig (die API kennt keine Freitextsuche). */
export interface AuditFilters {
  action: string;
  result: "" | AuditResult;
  actor: string;
  serverId: string;
  range: AuditRange;
  text: string;
  limit: number;
}

export type AuditRange = "all" | "1h" | "24h" | "7d" | "30d";

export const AUDIT_RESULTS: AuditResult[] = ["success", "failed", "denied"];

export const AUDIT_RANGES: { id: AuditRange; label: string }[] = [
  { id: "all", label: "Alle" },
  { id: "1h", label: "1 h" },
  { id: "24h", label: "24 h" },
  { id: "7d", label: "7 Tage" },
  { id: "30d", label: "30 Tage" },
];

const RANGE_SECONDS: Record<Exclude<AuditRange, "all">, number> = {
  "1h": 3600,
  "24h": 86_400,
  "7d": 604_800,
  "30d": 2_592_000,
};

export function emptyAuditFilters(): AuditFilters {
  return { action: "", result: "", actor: "", serverId: "", range: "all", text: "", limit: 100 };
}

/** Untergrenze (Unix-Sekunden) für einen Zeitraum, oder undefined für „alle". */
export function sinceFromRange(range: AuditRange, nowSec: number): number | undefined {
  if (range === "all") return undefined;
  return Math.max(0, Math.trunc(nowSec) - RANGE_SECONDS[range]);
}

/** CSS-Chip-Variante je Result: success→ok, denied→warn, failed→danger. */
export function auditResultClass(result: AuditResult): "ok" | "warn" | "danger" {
  if (result === "success") return "ok";
  if (result === "denied") return "warn";
  return "danger";
}

/**
 * Gruppen-Reihenfolge (erster Treffer gewinnt). Deckt Punkt- (`auth.login`) wie
 * Unterstrich-Actions (`pid_ban_add`) ab. `pid_ban` vor `ban`, damit PID-Bans
 * nicht in die Ban-Gruppe fallen.
 */
const ACTION_GROUPS: { group: string; prefixes: string[] }[] = [
  { group: "auth", prefixes: ["auth", "login", "logout"] },
  { group: "rcon", prefixes: ["rcon"] },
  { group: "pid_ban", prefixes: ["pid_ban"] },
  { group: "user", prefixes: ["user"] },
  { group: "server", prefixes: ["server"] },
  { group: "settings", prefixes: ["settings", "setting"] },
  { group: "geoip", prefixes: ["geoip"] },
  { group: "retention", prefixes: ["retention"] },
  { group: "announcer", prefixes: ["announcer"] },
  { group: "tickets", prefixes: ["tickets", "ticket"] },
  { group: "tac", prefixes: ["tac"] },
  { group: "command", prefixes: ["cmd", "command", "fbf"] },
];

/** Klassifiziert eine Action in eine Gruppe (für Action-Chips). */
export function auditActionGroup(action: string): string {
  const a = action.trim().toLowerCase();
  for (const { group, prefixes } of ACTION_GROUPS) {
    if (prefixes.some((p) => a === p || a.startsWith(`${p}.`) || a.startsWith(`${p}_`))) return group;
  }
  return "other";
}

/** Baut den API-Body aus den Filtern — nur gesetzte, von der API unterstützte Felder. */
export function buildAuditQuery(filters: AuditFilters, nowSec: number): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (filters.action.trim()) out.audit_action = filters.action.trim();
  if (filters.result) out.result = filters.result;
  if (filters.actor.trim()) out.actor = filters.actor.trim();
  if (filters.serverId.trim()) out.server_id = filters.serverId.trim();
  const since = sinceFromRange(filters.range, nowSec);
  if (since !== undefined) out.since = since;
  out.limit = Math.min(500, Math.max(1, Math.trunc(filters.limit) || 100));
  return out;
}

/** Clientseitiger Freitextfilter über Action/Actor/Target/Summary. */
export function matchesAuditText(entry: AuditEntry, text: string): boolean {
  const q = text.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    entry.action,
    entry.actorUsername,
    entry.serverId,
    entry.targetType,
    entry.targetId,
    entry.summary,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

/** metadata_json lesbar einrücken. Leeres/fehlendes → "". */
export function prettyMetadata(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Zeitstempel (Unix-Sekunden) lesbar; 0/NaN → Dash. */
export function formatAuditTs(ts: number): string {
  if (!ts || !Number.isFinite(ts)) return "—";
  return new Date(ts * 1000).toLocaleString();
}

/** Kurzes Action→Target-Label für die Tabelle. */
export function targetLabel(entry: AuditEntry): string {
  if (entry.targetType && entry.targetId) return `${entry.targetType}:${entry.targetId}`;
  return entry.targetId || entry.targetType || "—";
}
